import React, { createContext, useContext, useState, useEffect } from 'react';
import { collection, doc, getDoc, getDocs, query, where, addDoc, updateDoc, deleteDoc, setDoc, onSnapshot, orderBy, limit, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { useAuth } from './AuthContext';
import { useCoins } from './CoinContext';
import { useNotification } from './NotificationContext';
import { calculateCartFees, checkCoinRequirement, MARKETPLACE_FEES } from '../utils/marketplaceFees';
import { createStoreModel, createProductModel, createOrderModel, createCartItemModel, createAuctionModel, createBuyerRequestModel } from '../data/marketplaceModels';
import { notificationModel } from '../data/notificationModel';
import { createPackageSubscriptionModel, PACKAGE_TYPES } from '../data/packageModels';
import { createVideoCallAddonModel, VIDEO_CALL_ADDON_TYPES } from '../data/videoCallAddons';
import { checkPackageExpiration, isPackageValid, validateAndUpdateSubscription, getPackageInfo } from '../utils/packageExpirationUtils';

const MarketplaceContext = createContext();

export const MarketplaceProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const { coins, deductCoins } = useCoins();
  const { createNotification } = useNotification();
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(false);

  // ดึงข้อมูลตะกร้าจาก localStorage
  useEffect(() => {
    if (currentUser) {
      const savedCart = localStorage.getItem(`cart_${currentUser.uid}`);
      if (savedCart) {
        setCart(JSON.parse(savedCart));
      }
    }
  }, [currentUser]);

  // บันทึกตะกร้าใน localStorage
  useEffect(() => {
    if (currentUser && cart.length > 0) {
      localStorage.setItem(`cart_${currentUser.uid}`, JSON.stringify(cart));
    }
  }, [cart, currentUser]);

  /**
   * เปิดร้าน
   */
  const openStore = async (storeData) => {
    if (!currentUser) throw new Error('กรุณาเข้าสู่ระบบ');
    
    setLoading(true);
    try {
      // ตรวจสอบเหรียญ
      const feeCheck = checkCoinRequirement(coins, 'OPEN_STORE');
      if (!feeCheck.hasEnough) {
        throw new Error(feeCheck.message);
      }

      // หักเหรียญ
      await deductCoins(feeCheck.required);

      // สร้างร้าน
      const storeModel = createStoreModel({
        ...storeData,
        ownerId: currentUser.uid,
        ownerRole: currentUser.role
      });

      const storeRef = await addDoc(collection(db, 'stores'), storeModel);
      
      // อัพเดท user document
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        storeId: storeRef.id,
        hasStore: true
      });

      return storeRef.id;
    } catch (error) {
      console.error('Error opening store:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * อัพเดทข้อมูลร้าน
   */
  const updateStore = async (storeId, storeData) => {
    if (!currentUser) throw new Error('กรุณาเข้าสู่ระบบ');
    
    setLoading(true);
    try {
      const storeRef = doc(db, 'stores', storeId);
      const storeSnap = await getDoc(storeRef);
      
      if (!storeSnap.exists()) {
        throw new Error('ไม่พบร้านค้า');
      }
      
      const existingStore = storeSnap.data();
      
      // ตรวจสอบว่าเป็นเจ้าของร้าน
      if (existingStore.ownerId !== currentUser.uid) {
        throw new Error('คุณไม่มีสิทธิ์แก้ไขร้านค้านี้');
      }
      
      // อัพเดทข้อมูลร้าน
      await updateDoc(storeRef, {
        ...storeData,
        updatedAt: new Date()
      });
      
      return true;
    } catch (error) {
      console.error('Error updating store:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * ลงสินค้า
   */
  const listProduct = async (productData, storeId) => {
    if (!currentUser) throw new Error('กรุณาเข้าสู่ระบบ');
    
    setLoading(true);
    try {
      // ตรวจสอบ Package และวันสิ้นสุด
      const subscription = await getStoreSubscription(storeId);
      const packageInfo = getPackageInfo(subscription);
      
      if (!packageInfo.isValid || packageInfo.isExpired) {
        throw new Error('แพ็กเกจของคุณหมดอายุแล้ว กรุณาสมัครแพ็กเกจใหม่เพื่อใช้งาน');
      }

      // ตรวจสอบจำนวนสินค้าสูงสุด (maxActiveProducts)
      if (packageInfo.packageType !== PACKAGE_TYPES.FREE) {
        const { PACKAGES, PACKAGE_FEATURES } = await import('../data/packageModels');
        const packageFeatures = PACKAGES[packageInfo.packageType];
        const maxProducts = packageFeatures?.[PACKAGE_FEATURES.MAX_ACTIVE_PRODUCTS] || 0;
        
        if (maxProducts > 0 && maxProducts !== -1) {
          // นับจำนวนสินค้าที่เปิดขายอยู่
          const productsRef = collection(db, 'products');
          const activeProductsQuery = query(
            productsRef,
            where('storeId', '==', storeId),
            where('status', '==', 'active')
          );
          const activeProductsSnapshot = await getDocs(activeProductsQuery);
          const activeProductsCount = activeProductsSnapshot.size;
          
          if (activeProductsCount >= maxProducts) {
            throw new Error(`คุณได้ใช้จำนวนสินค้าสูงสุดแล้ว (${maxProducts} ชิ้น) กรุณาอัพเกรดแพ็กเกจเพื่อเพิ่มจำนวนสินค้า`);
          }
        }
      }

      // ตรวจสอบเหรียญ
      const feeCheck = checkCoinRequirement(coins, 'LIST_PRODUCT');
      if (!feeCheck.hasEnough) {
        throw new Error(feeCheck.message);
      }

      // หักเหรียญ
      await deductCoins(feeCheck.required);

      // Filter out undefined values before creating model
      const cleanProductData = { ...productData, storeId: storeId };
      Object.keys(cleanProductData).forEach(key => {
        if (cleanProductData[key] === undefined) {
          delete cleanProductData[key];
        }
      });
      
      // สร้างสินค้า
      const productModel = createProductModel(cleanProductData);

      const productRef = await addDoc(collection(db, 'products'), productModel);
      return productRef.id;
    } catch (error) {
      console.error('Error listing product:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * เพิ่มสินค้าลงตะกร้า
   */
  const addToCart = async (product) => {
    // ตรวจสอบว่าเป็นสินค้าของตัวเองหรือไม่
    if (currentUser && product.storeId) {
      try {
        // ดึงข้อมูล user เพื่อเช็ค storeId
        const userRef = doc(db, 'users', currentUser.uid);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
          const userData = userSnap.data();
          if (userData.storeId === product.storeId) {
            throw new Error('ไม่สามารถเพิ่มสินค้าของตัวเองลงตะกร้าได้');
          }
        }
      } catch (error) {
        // ถ้าเป็น error ที่เราสร้างขึ้น (ไม่สามารถเพิ่มสินค้าของตัวเอง) ให้ throw ต่อ
        if (error.message === 'ไม่สามารถเพิ่มสินค้าของตัวเองลงตะกร้าได้') {
          throw error;
        }
        // ถ้าเป็น error อื่นๆ (เช่น permission denied) ให้ log และ continue
        console.warn('Error checking store ownership:', error);
      }
    }
    
    // ตรวจสอบ minimumOrderQuantity
    if (product.minimumOrderQuantity && product.minimumOrderQuantity > 0) {
      const existingItem = cart.find(item => item.productId === product.id);
      const currentQuantity = existingItem ? existingItem.quantity : 0;
      
      if (currentQuantity + 1 < product.minimumOrderQuantity) {
        throw new Error(`ต้องสั่งซื้อขั้นต่ำ ${product.minimumOrderQuantity} ${product.unit}`);
      }
    }
    
    const existingItem = cart.find(item => item.productId === product.id);
    
    if (existingItem) {
      // อัพเดทจำนวน
      const newQuantity = existingItem.quantity + 1;
      setCart(cart.map(item =>
        item.productId === product.id
          ? { ...item, quantity: newQuantity, subtotal: newQuantity * item.price }
          : item
      ));
    } else {
      // เพิ่มรายการใหม่
      const cartItem = createCartItemModel({
        productId: product.id,
        productName: product.name,
        storeId: product.storeId,
        storeName: product.storeName,
        price: product.price,
        quantity: 1,
        image: product.images?.[0] || '',
        unit: product.unit
      });
      setCart([...cart, cartItem]);
    }
  };

  /**
   * ลบสินค้าออกจากตะกร้า
   */
  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.productId !== productId));
  };

  /**
   * อัพเดทจำนวนสินค้าในตะกร้า
   */
  const updateCartQuantity = async (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    
    // ตรวจสอบ minimumOrderQuantity
    const item = cart.find(item => item.productId === productId);
    if (item) {
      // ดึงข้อมูล product เพื่อเช็ค minimumOrderQuantity
      try {
        const productRef = doc(db, 'products', productId);
        const productSnap = await getDoc(productRef);
        if (productSnap.exists()) {
          const productData = productSnap.data();
          if (productData.minimumOrderQuantity && productData.minimumOrderQuantity > 0) {
            if (quantity < productData.minimumOrderQuantity) {
              throw new Error(`ต้องสั่งซื้อขั้นต่ำ ${productData.minimumOrderQuantity} ${productData.unit || item.unit}`);
            }
          }
        }
      } catch (error) {
        if (error.message.includes('ต้องสั่งซื้อขั้นต่ำ')) {
          throw error;
        }
        // ถ้า error อื่นๆ ให้ข้ามไป
      }
    }
    
    setCart(cart.map(item =>
      item.productId === productId
        ? { ...item, quantity, subtotal: quantity * item.price }
        : item
    ));
  };

  /**
   * ล้างตะกร้า
   */
  const clearCart = () => {
    setCart([]);
  };

  /**
   * คำนวณยอดรวมตะกร้า
   */
  const getCartTotal = () => {
    return calculateCartFees(cart);
  };

  /**
   * สร้างออเดอร์
   */
  const createOrder = async (orderData) => {
    if (!currentUser) throw new Error('กรุณาเข้าสู่ระบบ');
    
    setLoading(true);
    try {
      // ใช้ items จาก orderData หรือ cart
      const orderItems = orderData.items || cart;
      
      if (!orderItems || orderItems.length === 0) {
        throw new Error('ไม่มีสินค้าในออเดอร์');
      }

      // คำนวณค่าธรรมเนียมจาก items ที่ส่งมา
      const orderSubtotal = orderItems.reduce((sum, item) => {
        return sum + ((item.price || 0) * (item.quantity || 0));
      }, 0);
      
      const feeCheck = checkCoinRequirement(coins, 'ORDER_FEE', { orderTotal: orderSubtotal });
      
      if (!feeCheck.hasEnough) {
        throw new Error(feeCheck.message);
      }

      // หักเหรียญ
      await deductCoins(feeCheck.required);

      // ดึงข้อมูล store และ seller จาก items (ใช้ storeId แรก)
      let sellerId = '';
      let storeId = '';
      let storeName = '';
      
      if (orderItems.length > 0 && orderItems[0].storeId) {
        storeId = orderItems[0].storeId;
        storeName = orderItems[0].storeName || '';
        
        // ดึงข้อมูล store เพื่อหา ownerId (sellerId)
        try {
          const storeRef = doc(db, 'stores', storeId);
          const storeSnap = await getDoc(storeRef);
          if (storeSnap.exists()) {
            const storeData = storeSnap.data();
            sellerId = storeData.ownerId || '';
            if (!storeName) storeName = storeData.name || '';
          }
        } catch (error) {
          console.error('Error fetching store:', error);
        }
      }

      // คำนวณ total
      const adminFee = orderData.adminFee || 0;
      const orderFee = feeCheck.required;
      const orderTotal = orderSubtotal + adminFee; // ไม่รวม fee เพราะหักจากเหรียญแล้ว

      // สร้างออเดอร์
      const orderModel = createOrderModel({
        ...orderData,
        buyerId: currentUser.uid,
        buyerName: currentUser.displayName || currentUser.email,
        sellerId: sellerId,
        sellerName: '', // จะดึงจาก store ภายหลัง
        storeId: storeId,
        storeName: storeName,
        items: orderItems,
        subtotal: orderSubtotal,
        fee: orderFee,
        total: orderTotal
      });

      const orderRef = await addDoc(collection(db, 'orders'), orderModel);
      
      // ส่งการแจ้งเตือนให้ผู้ขาย
      try {
        await createNotification(
          orderModel.sellerId,
          notificationModel.types.ORDER_NEW,
          'ออเดอร์ใหม่',
          `คุณมีออเดอร์ใหม่จาก ${orderModel.buyerName} จำนวน ${orderModel.items.length} รายการ`,
          { orderId: orderRef.id, storeId: orderModel.storeId }
        );
      } catch (notifError) {
        console.error('Error creating order notification:', notifError);
      }

      // ส่งการแจ้งเตือนให้แอดมินถ้าใช้ admin middleman
      if (orderModel.paymentMethod === 'admin_middleman' && adminFee > 0) {
        try {
          // ดึงรายการแอดมินทั้งหมด
          const { getUsersByRole } = await import('../../utils/roleManager');
          const { ROLES } = await import('../../utils/roleManager');
          const admins = await getUsersByRole(ROLES.ADMIN);
          
          // ส่ง notification ให้แอดมินทุกคน
          const adminNotifications = admins.map(admin => 
            createNotification(
              admin.id,
              notificationModel.types.ADMIN_MIDDLEMAN_ORDER,
              'ออเดอร์ที่ใช้แอดมินเป็นตัวกลาง',
              `ออเดอร์ #${orderRef.id} จาก ${orderModel.buyerName} ใช้แอดมินเป็นตัวกลาง ค่าธรรมเนียม ${adminFee.toLocaleString()} บาท`,
              { 
                orderId: orderRef.id, 
                storeId: orderModel.storeId,
                buyerId: orderModel.buyerId,
                buyerName: orderModel.buyerName,
                adminFee: adminFee,
                total: orderTotal
              }
            )
          );
          
          // สร้าง notifications ทั้งหมด
          const { collection, addDoc } = await import('firebase/firestore');
          const notificationsRef = collection(db, 'notifications');
          await Promise.all(adminNotifications.map(notif => addDoc(notificationsRef, notif)));
        } catch (adminNotifError) {
          console.error('Error creating admin middleman notification:', adminNotifError);
        }
      }
      
      // ล้างตะกร้าเฉพาะ items ที่ใช้ในออเดอร์นี้ (ถ้าใช้ cart)
      if (!orderData.items) {
        clearCart();
      } else {
        // ลบเฉพาะ items ที่ใช้ในออเดอร์นี้
        const orderItemIds = new Set(orderData.items.map(item => item.productId));
        setCart(prevCart => prevCart.filter(item => !orderItemIds.has(item.productId)));
      }
      
      return orderRef.id;
    } catch (error) {
      console.error('Error creating order:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * อัพเดทสถานะออเดอร์
   */
  const updateOrderStatus = async (orderId, status, additionalData = {}) => {
    if (!currentUser) throw new Error('กรุณาเข้าสู่ระบบ');
    
    setLoading(true);
    try {
      const orderRef = doc(db, 'orders', orderId);
      const orderSnap = await getDoc(orderRef);
      
      if (!orderSnap.exists()) {
        throw new Error('ไม่พบออเดอร์');
      }
      
      const orderData = orderSnap.data();
      await updateDoc(orderRef, {
        status,
        ...additionalData,
        updatedAt: new Date()
      });
      
      // ส่งการแจ้งเตือนให้ผู้ซื้อ/ผู้ขาย
      const statusMessages = {
        'paid': 'ออเดอร์ได้รับการชำระเงินแล้ว',
        'processing': 'ออเดอร์กำลังเตรียมจัดส่ง',
        'shipping': 'ออเดอร์กำลังจัดส่ง',
        'delivered': 'ออเดอร์จัดส่งสำเร็จแล้ว',
        'cancelled': 'ออเดอร์ถูกยกเลิก'
      };
      
      const message = statusMessages[status] || `สถานะออเดอร์เปลี่ยนเป็น: ${status}`;
      const notifyUserId = currentUser.uid === orderData.buyerId ? orderData.sellerId : orderData.buyerId;
      
      try {
        await createNotification(
          notifyUserId,
          notificationModel.types.ORDER_STATUS,
          'อัพเดทสถานะออเดอร์',
          message,
          { orderId, status }
        );
      } catch (notifError) {
        console.error('Error creating status notification:', notifError);
      }
    } catch (error) {
      console.error('Error updating order status:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * เปิดประมูล
   */
  const createAuction = async (auctionData) => {
    if (!currentUser) throw new Error('กรุณาเข้าสู่ระบบ');
    
    setLoading(true);
    try {
      // ตรวจสอบเหรียญ
      const feeCheck = checkCoinRequirement(coins, 'CREATE_AUCTION');
      if (!feeCheck.hasEnough) {
        throw new Error(feeCheck.message);
      }

      // หักเหรียญ
      await deductCoins(feeCheck.required);

      // สร้างประมูล
      const auctionModel = createAuctionModel({
        ...auctionData,
        sellerId: currentUser.uid,
        sellerFeePaid: true
      });

      const auctionRef = await addDoc(collection(db, 'auctions'), auctionModel);
      
      // อัพเดทสถานะสินค้า
      if (auctionData.productId) {
        const productRef = doc(db, 'products', auctionData.productId);
        await updateDoc(productRef, {
          status: 'auction',
          auctionId: auctionRef.id,
          isAuction: true
        });
      }
      
      return auctionRef.id;
    } catch (error) {
      console.error('Error creating auction:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * เสนอราคาประมูล
   */
  const placeBid = async (auctionId, bidAmount) => {
    if (!currentUser) throw new Error('กรุณาเข้าสู่ระบบ');
    
    setLoading(true);
    try {
      // ดึงข้อมูลประมูล
      const auctionRef = doc(db, 'auctions', auctionId);
      const auctionSnap = await getDoc(auctionRef);
      
      if (!auctionSnap.exists()) {
        throw new Error('ไม่พบประมูล');
      }
      
      const auctionData = auctionSnap.data();
      
      // ตรวจสอบว่าไม่ใช่ประมูลของตัวเอง
      if (auctionData.sellerId === currentUser.uid) {
        throw new Error('ไม่สามารถเสนอราคาในประมูลที่ตัวเองเปิดได้');
      }
      
      // ตรวจสอบเหรียญ
      const feeCheck = checkCoinRequirement(coins, 'PLACE_BID');
      if (!feeCheck.hasEnough) {
        throw new Error(feeCheck.message);
      }

      // หักเหรียญ
      await deductCoins(feeCheck.required);

      const bids = auctionData.bids || [];
      
      // เพิ่มการเสนอราคา
      bids.push({
        userId: currentUser.uid,
        userName: currentUser.displayName || currentUser.email,
        amount: bidAmount,
        timestamp: new Date()
      });

      await updateDoc(auctionRef, {
        bids,
        currentBid: bidAmount,
        highestBidder: currentUser.uid,
        bidFees: {
          ...auctionData.bidFees,
          [currentUser.uid]: { paid: true, refunded: false }
        },
        updatedAt: new Date()
      });
      
      // ส่งการแจ้งเตือนให้ผู้ขาย
      try {
        if (!auctionData.sellerId) {
          console.error('Cannot create notification: sellerId is missing', auctionData);
        } else {
          console.log('Creating bid notification for seller:', auctionData.sellerId);
          await createNotification(
            auctionData.sellerId,
            notificationModel.types.BID_NEW,
            'มีการเสนอราคาใหม่',
            `${currentUser.displayName || currentUser.email} เสนอราคา ฿${bidAmount.toLocaleString()} ในประมูลของคุณ`,
            { auctionId, bidAmount, bidderId: currentUser.uid, productName: auctionData.productName }
          );
          console.log('Bid notification created successfully');
        }
      } catch (notifError) {
        console.error('Error creating bid notification:', notifError);
        console.error('Notification error details:', {
          sellerId: auctionData.sellerId,
          auctionId,
          bidAmount,
          error: notifError.message,
          stack: notifError.stack
        });
        // Don't throw - allow bid to succeed even if notification fails
      }
      
      return true;
    } catch (error) {
      console.error('Error placing bid:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * จบประมูล (อัพเดทสถานะเมื่อหมดเวลา)
   */
  const endAuction = async (auctionId) => {
    if (!currentUser) throw new Error('กรุณาเข้าสู่ระบบ');
    
    setLoading(true);
    try {
      const auctionRef = doc(db, 'auctions', auctionId);
      const auctionSnap = await getDoc(auctionRef);
      
      if (!auctionSnap.exists()) {
        throw new Error('ไม่พบประมูล');
      }
      
      const auctionData = auctionSnap.data();
      
      // ตรวจสอบว่าเป็นเจ้าของประมูล
      if (auctionData.sellerId !== currentUser.uid) {
        throw new Error('คุณไม่มีสิทธิ์จัดการประมูลนี้');
      }
      
      // อัพเดทสถานะ
      await updateDoc(auctionRef, {
        status: 'ended',
        isEnded: true,
        updatedAt: new Date()
      });
      
      // ส่งการแจ้งเตือนให้ผู้ชนะ (ถ้ามี)
      if (auctionData.highestBidder) {
        try {
          await createNotification(
            auctionData.highestBidder,
            notificationModel.types.AUCTION_WON,
            'คุณชนะประมูล!',
            `คุณชนะประมูล "${auctionData.productName}" ด้วยราคา ฿${auctionData.currentBid?.toLocaleString()}`,
            { auctionId, winningPrice: auctionData.currentBid }
          );
        } catch (notifError) {
          console.error('Error creating winner notification:', notifError);
        }
      }
      
      return true;
    } catch (error) {
      console.error('Error ending auction:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * ยืนยันผู้ชนะประมูล
   */
  const confirmWinner = async (auctionId, winnerId) => {
    if (!currentUser) throw new Error('กรุณาเข้าสู่ระบบ');
    
    setLoading(true);
    try {
      const auctionRef = doc(db, 'auctions', auctionId);
      const auctionSnap = await getDoc(auctionRef);
      
      if (!auctionSnap.exists()) {
        throw new Error('ไม่พบประมูล');
      }
      
      const auctionData = auctionSnap.data();
      
      // ตรวจสอบว่าเป็นเจ้าของประมูล
      if (auctionData.sellerId !== currentUser.uid) {
        throw new Error('คุณไม่มีสิทธิ์จัดการประมูลนี้');
      }
      
      // อัพเดทผู้ชนะ
      await updateDoc(auctionRef, {
        confirmedWinner: winnerId,
        status: 'winner_confirmed',
        updatedAt: new Date()
      });
      
      // ส่งการแจ้งเตือนให้ผู้ชนะ
      try {
        await createNotification(
          winnerId,
          notificationModel.types.AUCTION_WINNER_CONFIRMED,
          'ผู้ขายยืนยันคุณเป็นผู้ชนะ',
          `ผู้ขายยืนยันว่าคุณชนะประมูล "${auctionData.productName}"`,
          { auctionId }
        );
      } catch (notifError) {
        console.error('Error creating confirmation notification:', notifError);
      }
      
      return true;
    } catch (error) {
      console.error('Error confirming winner:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * สร้างออเดอร์จากประมูล
   */
  const createOrderFromAuction = async (auctionId, shippingAddress) => {
    if (!currentUser) throw new Error('กรุณาเข้าสู่ระบบ');
    
    setLoading(true);
    try {
      const auctionRef = doc(db, 'auctions', auctionId);
      const auctionSnap = await getDoc(auctionRef);
      
      if (!auctionSnap.exists()) {
        throw new Error('ไม่พบประมูล');
      }
      
      const auctionData = auctionSnap.data();
      
      // ตรวจสอบว่าเป็นเจ้าของประมูล
      if (auctionData.sellerId !== currentUser.uid) {
        throw new Error('คุณไม่มีสิทธิ์จัดการประมูลนี้');
      }
      
      // ตรวจสอบว่ามีผู้ชนะ
      if (!auctionData.highestBidder) {
        throw new Error('ยังไม่มีผู้ชนะประมูล');
      }
      
      // ดึงข้อมูลผู้ชนะ
      const winnerRef = doc(db, 'users', auctionData.highestBidder);
      const winnerSnap = await getDoc(winnerRef);
      const winnerData = winnerSnap.exists() ? winnerSnap.data() : {};
      
      // ดึงข้อมูลสินค้า
      const productRef = doc(db, 'products', auctionData.productId);
      const productSnap = await getDoc(productRef);
      const productData = productSnap.exists() ? productSnap.data() : {};
      
      // ดึงข้อมูลร้าน
      const storeRef = doc(db, 'stores', auctionData.storeId);
      const storeSnap = await getDoc(storeRef);
      const storeData = storeSnap.exists() ? storeSnap.data() : {};
      
      // สร้างออเดอร์
      const orderModel = createOrderModel({
        buyerId: auctionData.highestBidder,
        buyerName: winnerData.displayName || winnerData.email || 'ผู้ชนะประมูล',
        sellerId: auctionData.sellerId,
        sellerName: currentUser.displayName || currentUser.email,
        storeId: auctionData.storeId,
        storeName: storeData.name || 'ร้านค้า',
        items: [{
          productId: auctionData.productId,
          name: auctionData.productName,
          price: auctionData.currentBid,
          quantity: 1,
          subtotal: auctionData.currentBid
        }],
        subtotal: auctionData.currentBid,
        fee: 0, // ไม่มีค่าธรรมเนียมเพิ่มเติมสำหรับประมูล
        total: auctionData.currentBid,
        status: 'pending_payment',
        paymentStatus: 'pending',
        shippingAddress: shippingAddress || '',
        notes: `ออเดอร์จากประมูล: ${auctionId}`
      });
      
      const orderRef = await addDoc(collection(db, 'orders'), orderModel);
      
      // อัพเดทประมูลให้เชื่อมโยงกับออเดอร์
      await updateDoc(auctionRef, {
        orderId: orderRef.id,
        status: 'order_created',
        updatedAt: new Date()
      });
      
      // ส่งการแจ้งเตือนให้ผู้ชนะ
      try {
        await createNotification(
          auctionData.highestBidder,
          notificationModel.types.ORDER_CREATED,
          'ออเดอร์ถูกสร้างแล้ว',
          `ออเดอร์จากประมูล "${auctionData.productName}" ถูกสร้างแล้ว กรุณาชำระเงิน`,
          { orderId: orderRef.id, auctionId }
        );
      } catch (notifError) {
        console.error('Error creating order notification:', notifError);
      }
      
      return orderRef.id;
    } catch (error) {
      console.error('Error creating order from auction:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * ยกเลิกประมูล
   */
  const cancelAuction = async (auctionId, reason) => {
    if (!currentUser) throw new Error('กรุณาเข้าสู่ระบบ');
    
    setLoading(true);
    try {
      const auctionRef = doc(db, 'auctions', auctionId);
      const auctionSnap = await getDoc(auctionRef);
      
      if (!auctionSnap.exists()) {
        throw new Error('ไม่พบประมูล');
      }
      
      const auctionData = auctionSnap.data();
      
      // ตรวจสอบว่าเป็นเจ้าของประมูล
      if (auctionData.sellerId !== currentUser.uid) {
        throw new Error('คุณไม่มีสิทธิ์จัดการประมูลนี้');
      }
      
      // ตรวจสอบเหรียญสำหรับค่าปรับ
      const penaltyCheck = checkCoinRequirement(coins, 'CANCEL_AUCTION_PENALTY');
      if (!penaltyCheck.hasEnough) {
        throw new Error(penaltyCheck.message);
      }
      
      // หักค่าปรับจากเจ้าของประมูล
      await deductCoins(penaltyCheck.required);
      
      // คืนเหรียญให้ผู้ประมูลทั้งหมด
      if (auctionData.bids && auctionData.bids.length > 0) {
        const bidFees = auctionData.bidFees || {};
        const bidFeeAmount = MARKETPLACE_FEES.PLACE_BID.default;
        
        // เก็บรายชื่อผู้ที่ต้องคืนเหรียญ (ไม่ซ้ำ)
        const uniqueBidders = [...new Set(auctionData.bids.map(bid => bid.userId))];
        
        for (const bidderId of uniqueBidders) {
          // ตรวจสอบว่าผู้ประมูลจ่ายค่าธรรมเนียมแล้วและยังไม่ได้รับคืน
          const bidFee = bidFees[bidderId];
          if (bidFee && bidFee.paid && !bidFee.refunded) {
            try {
              // คืนเหรียญให้ผู้ประมูล
              const bidderRef = doc(db, 'users', bidderId);
              const bidderSnap = await getDoc(bidderRef);
              
              if (bidderSnap.exists()) {
                const bidderData = bidderSnap.data();
                const currentBidderCoins = bidderData.coins || 0;
                await updateDoc(bidderRef, {
                  coins: currentBidderCoins + bidFeeAmount,
                  updatedAt: new Date()
                });
              } else {
                // ถ้ายังไม่มี user document ให้สร้างใหม่
                await setDoc(bidderRef, {
                  uid: bidderId,
                  coins: bidFeeAmount,
                  createdAt: new Date(),
                  updatedAt: new Date()
                });
              }
              
              // อัพเดทสถานะ refund ใน auction
              bidFees[bidderId] = {
                ...bidFee,
                refunded: true,
                refundedAt: new Date()
              };
            } catch (refundError) {
              console.error(`Error refunding coins to bidder ${bidderId}:`, refundError);
              // ยังคงดำเนินการต่อแม้จะคืนเหรียญไม่สำเร็จ
            }
          }
        }
        
        // อัพเดท bidFees ใน auction
        await updateDoc(auctionRef, {
          bidFees,
          updatedAt: new Date()
        });
      }
      
      // อัพเดทสถานะประมูล
      await updateDoc(auctionRef, {
        status: 'cancelled',
        isEnded: true,
        cancellationReason: reason || '',
        penaltyPaid: penaltyCheck.required,
        refundsProcessed: true,
        updatedAt: new Date()
      });
      
      // อัพเดทสถานะสินค้า
      if (auctionData.productId) {
        const productRef = doc(db, 'products', auctionData.productId);
        await updateDoc(productRef, {
          status: 'active',
          auctionId: null,
          isAuction: false
        });
      }
      
      // ส่งการแจ้งเตือนให้ผู้เสนอราคาทั้งหมด
      if (auctionData.bids && auctionData.bids.length > 0) {
        const bidderIds = [...new Set(auctionData.bids.map(bid => bid.userId))];
        for (const bidderId of bidderIds) {
          try {
            await createNotification(
              bidderId,
              notificationModel.types.AUCTION_CANCELLED,
              'ประมูลถูกยกเลิก - เหรียญถูกคืนแล้ว',
              `ประมูล "${auctionData.productName}" ถูกยกเลิกโดยผู้ขาย เหรียญที่ใช้เสนอราคาถูกคืนให้คุณแล้ว`,
              { auctionId, refunded: true }
            );
          } catch (notifError) {
            console.error('Error creating cancellation notification:', notifError);
          }
        }
      }
      
      return {
        success: true,
        penaltyPaid: penaltyCheck.required,
        refundsCount: auctionData.bids ? [...new Set(auctionData.bids.map(bid => bid.userId))].length : 0
      };
    } catch (error) {
      console.error('Error cancelling auction:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * ดึง Package Subscription ของร้าน
   */
  const getStoreSubscription = async (storeId) => {
    if (!currentUser) throw new Error('กรุณาเข้าสู่ระบบ');
    
    try {
      // ตรวจสอบ store document ก่อน (เพราะ packageType ถูกเก็บไว้ที่นี่)
      const storeRef = doc(db, 'stores', storeId);
      const storeSnap = await getDoc(storeRef);
      
      if (storeSnap.exists()) {
        const storeData = storeSnap.data();
        // ถ้า store มี packageType ให้ใช้ค่านั้น
        if (storeData.packageType) {
          return {
            storeId,
            packageType: storeData.packageType,
            status: 'active',
            startDate: new Date(),
            endDate: null,
            autoRenew: false
          };
        }
      }
      
      // ถ้า store ไม่มี packageType ให้ตรวจสอบ subscription collection
      try {
        const subscriptionsRef = collection(db, 'storeSubscriptions');
        const q = query(
          subscriptionsRef,
          where('storeId', '==', storeId),
          where('status', '==', 'active')
        );
        
        const snapshot = await getDocs(q);
        if (!snapshot.empty) {
          // หา subscription ล่าสุด (ถ้ามีหลายตัว)
          const subscriptions = snapshot.docs.map(doc => ({
            id: doc.id,
            ref: doc.ref,
            ...doc.data()
          }));
          
          // เรียงตาม startDate (ถ้ามี)
          const sorted = subscriptions.sort((a, b) => {
            const aDate = a.startDate?.toDate?.() || new Date(a.startDate || 0);
            const bDate = b.startDate?.toDate?.() || new Date(b.startDate || 0);
            return bDate - aDate;
          });
          
          // ตรวจสอบวันสิ้นสุดของ subscription ล่าสุด
          const latestSubscription = sorted[0];
          const expirationCheck = checkPackageExpiration(latestSubscription);
          
          // ถ้าหมดอายุ ให้อัพเดท status
          if (expirationCheck.isExpired) {
            await validateAndUpdateSubscription(latestSubscription, latestSubscription.ref);
            // ถ้าหมดอายุ ให้ return Free package
            return {
              storeId,
              packageType: PACKAGE_TYPES.FREE,
              status: 'expired',
              startDate: latestSubscription.startDate,
              endDate: latestSubscription.endDate,
              autoRenew: false
            };
          }
          
          // ตรวจสอบและส่ง notification ถ้าใกล้หมดอายุ (7, 3, 1 วัน)
          if (expirationCheck.daysRemaining !== null && expirationCheck.daysRemaining <= 7) {
            try {
              const { checkAndNotifyExpiringSubscription } = await import('../utils/subscriptionNotificationUtils');
              const storeData = storeSnap.data();
              if (storeData.ownerId) {
                await checkAndNotifyExpiringSubscription(
                  latestSubscription,
                  storeId,
                  storeData.ownerId,
                  createNotification
                );
              }
            } catch (notifError) {
              console.warn('Error checking expiration notification:', notifError);
              // Continue anyway - notification is not critical
            }
          }
          
          // ถ้ายังไม่หมดอายุ return subscription
          return latestSubscription;
        }
      } catch (subscriptionError) {
        console.warn('Error fetching subscription collection:', subscriptionError);
        // Continue to return Free package
      }
      
      // ถ้าไม่มี subscription ให้ return Free package
      return {
        storeId,
        packageType: PACKAGE_TYPES.FREE,
        status: 'active',
        startDate: new Date(),
        endDate: null,
        autoRenew: false
      };
    } catch (error) {
      console.error('Error fetching store subscription:', error);
      // Return Free package as fallback
      return {
        storeId,
        packageType: PACKAGE_TYPES.FREE,
        status: 'active',
        startDate: new Date(),
        endDate: null,
        autoRenew: false
      };
    }
  };

  /**
   * สมัคร Package Subscription
   */
  const subscribeToPackage = async (storeId, packageType, paymentMethod = 'bank', transactionId = '', endDate = null, trialDays = null) => {
    if (!currentUser) throw new Error('กรุณาเข้าสู่ระบบ');
    
    setLoading(true);
    try {
      // ตรวจสอบว่าเป็นเจ้าของร้าน
      const storeRef = doc(db, 'stores', storeId);
      const storeSnap = await getDoc(storeRef);
      
      if (!storeSnap.exists()) {
        throw new Error('ไม่พบร้านค้า');
      }
      
      const storeData = storeSnap.data();
      if (storeData.ownerId !== currentUser.uid) {
        throw new Error('คุณไม่มีสิทธิ์สมัคร Package สำหรับร้านนี้');
      }

      // ถ้าเป็น Free package ไม่ต้องสร้าง subscription
      if (packageType === PACKAGE_TYPES.FREE) {
        // อัพเดท store ให้มี packageType
        await updateDoc(storeRef, {
          packageType: PACKAGE_TYPES.FREE,
          updatedAt: new Date()
        });
        return { success: true, packageType: PACKAGE_TYPES.FREE };
      }

      // คำนวณ endDate (ถ้ามี trialDays)
      let calculatedEndDate = endDate;
      if (trialDays && trialDays > 0) {
        const startDate = new Date();
        calculatedEndDate = new Date();
        calculatedEndDate.setDate(calculatedEndDate.getDate() + trialDays);
      }

      // สำหรับ Paid packages - สร้าง subscription
      const now = Timestamp.now(); // ใช้ Firestore Timestamp
      const startDateTimestamp = Timestamp.now();
      const endDateTimestamp = calculatedEndDate ? Timestamp.fromDate(calculatedEndDate) : null;
      
      const subscriptionModel = createPackageSubscriptionModel({
        storeId,
        packageType,
        status: 'active',
        startDate: startDateTimestamp, // วันที่สมัคร (Firestore Timestamp)
        endDate: endDateTimestamp, // null = unlimited, หรือวันที่สิ้นสุด trial (Firestore Timestamp)
        autoRenew: !calculatedEndDate, // ถ้ามี endDate (trial) ไม่ auto-renew
        paymentMethod,
        transactionId,
        createdAt: now
      });
      
      console.log('📅 Creating subscription:', {
        storeId,
        packageType,
        startDate: startDateTimestamp.toDate().toLocaleString('th-TH'),
        endDate: endDateTimestamp ? endDateTimestamp.toDate().toLocaleString('th-TH') : 'unlimited',
        trialDays
      });

      // ตรวจสอบว่ามี subscription เก่าหรือไม่
      const subscriptionsRef = collection(db, 'storeSubscriptions');
      const q = query(
        subscriptionsRef,
        where('storeId', '==', storeId),
        where('status', '==', 'active')
      );
      
      const existingSubs = await getDocs(q);
      
      if (!existingSubs.empty) {
        // อัพเดท subscription เก่าให้เป็น expired
        const updatePromises = existingSubs.docs.map(doc => 
          updateDoc(doc.ref, {
            status: 'expired',
            endDate: new Date(),
            updatedAt: new Date()
          })
        );
        await Promise.all(updatePromises);
      }

      // สร้าง subscription ใหม่
      const subscriptionRef = await addDoc(subscriptionsRef, subscriptionModel);

      // อัพเดท store ให้มี packageType
      await updateDoc(storeRef, {
        packageType: packageType,
        updatedAt: new Date()
      });

      return {
        success: true,
        subscriptionId: subscriptionRef.id,
        packageType: packageType
      };
    } catch (error) {
      console.error('Error subscribing to package:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * สมัคร Video Call Add-on
   */
  const subscribeToVideoCallAddon = async (storeId, addonId, paymentMethod = 'bank', transactionId = '') => {
    if (!currentUser) throw new Error('กรุณาเข้าสู่ระบบ');
    
    setLoading(true);
    try {
      // ตรวจสอบว่าเป็นเจ้าของร้าน
      const storeRef = doc(db, 'stores', storeId);
      const storeSnap = await getDoc(storeRef);
      
      if (!storeSnap.exists()) {
        throw new Error('ไม่พบร้านค้า');
      }
      
      const storeData = storeSnap.data();
      if (storeData.ownerId !== currentUser.uid) {
        throw new Error('คุณไม่มีสิทธิ์สมัคร Add-on สำหรับร้านนี้');
      }

      // สร้าง addon subscription model
      const addonModel = createVideoCallAddonModel({
        storeId,
        addonId,
        status: 'active',
        startDate: new Date(),
        endDate: null, // null = unlimited (จะจัดการ expiry ภายหลัง)
        autoRenew: true,
        paymentMethod,
        transactionId,
        callsUsed: 0,
        callsRemaining: 0 // จะคำนวณจาก addon features
      });

      // ตรวจสอบว่ามี addon subscription เก่าหรือไม่
      const addonsRef = collection(db, 'storeVideoCallAddons');
      const q = query(
        addonsRef,
        where('storeId', '==', storeId),
        where('status', '==', 'active')
      );
      
      const existingAddons = await getDocs(q);
      
      if (!existingAddons.empty) {
        // อัพเดท addon subscription เก่าให้เป็น expired
        const updatePromises = existingAddons.docs.map(doc => 
          updateDoc(doc.ref, {
            status: 'expired',
            endDate: new Date(),
            updatedAt: new Date()
          })
        );
        await Promise.all(updatePromises);
      }

      // คำนวณ calls remaining จาก addon features
      const { VIDEO_CALL_ADDONS } = require('../data/videoCallAddons');
      const addon = VIDEO_CALL_ADDONS[addonId];
      if (addon) {
        addonModel.callsRemaining = addon.features.callsPerMonth === -1 
          ? -1 // ไม่จำกัด
          : addon.features.callsPerMonth;
      }

      // สร้าง addon subscription ใหม่
      const addonRef = await addDoc(addonsRef, addonModel);

      return {
        success: true,
        addonSubscriptionId: addonRef.id,
        addonId: addonId
      };
    } catch (error) {
      console.error('Error subscribing to video call addon:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * ยกเลิก Video Call Add-on
   */
  const cancelVideoCallAddon = async (storeId) => {
    if (!currentUser) throw new Error('กรุณาเข้าสู่ระบบ');
    
    setLoading(true);
    try {
      // ตรวจสอบว่าเป็นเจ้าของร้าน
      const storeRef = doc(db, 'stores', storeId);
      const storeSnap = await getDoc(storeRef);
      
      if (!storeSnap.exists()) {
        throw new Error('ไม่พบร้านค้า');
      }
      
      const storeData = storeSnap.data();
      if (storeData.ownerId !== currentUser.uid) {
        throw new Error('คุณไม่มีสิทธิ์ยกเลิก Add-on สำหรับร้านนี้');
      }

      // หา active addon subscription
      const addonsRef = collection(db, 'storeVideoCallAddons');
      const q = query(
        addonsRef,
        where('storeId', '==', storeId),
        where('status', '==', 'active')
      );
      
      const existingAddons = await getDocs(q);
      
      if (existingAddons.empty) {
        throw new Error('ไม่พบ Add-on ที่ใช้งานอยู่');
      }

      // อัพเดท status เป็น cancelled
      const updatePromises = existingAddons.docs.map(doc => 
        updateDoc(doc.ref, {
          status: 'cancelled',
          endDate: new Date(),
          updatedAt: new Date()
        })
      );
      await Promise.all(updatePromises);

      return { success: true };
    } catch (error) {
      console.error('Error cancelling video call addon:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * ยกเลิก Package Subscription
   */
  const cancelSubscription = async (storeId) => {
    if (!currentUser) throw new Error('กรุณาเข้าสู่ระบบ');
    
    setLoading(true);
    try {
      // ตรวจสอบว่าเป็นเจ้าของร้าน
      const storeRef = doc(db, 'stores', storeId);
      const storeSnap = await getDoc(storeRef);
      
      if (!storeSnap.exists()) {
        throw new Error('ไม่พบร้านค้า');
      }
      
      const storeData = storeSnap.data();
      if (storeData.ownerId !== currentUser.uid) {
        throw new Error('คุณไม่มีสิทธิ์ยกเลิก Package สำหรับร้านนี้');
      }

      // หา active subscription
      const subscriptionsRef = collection(db, 'storeSubscriptions');
      const q = query(
        subscriptionsRef,
        where('storeId', '==', storeId),
        where('status', '==', 'active')
      );
      
      const snapshot = await getDocs(q);
      
      if (!snapshot.empty) {
        // อัพเดท subscription ให้เป็น cancelled
        const updatePromises = snapshot.docs.map(doc => 
          updateDoc(doc.ref, {
            status: 'cancelled',
            endDate: new Date(),
            autoRenew: false,
            updatedAt: new Date()
          })
        );
        await Promise.all(updatePromises);
      }

      // อัพเดท store ให้เป็น Free package
      await updateDoc(storeRef, {
        packageType: PACKAGE_TYPES.FREE,
        updatedAt: new Date()
      });

      return { success: true };
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * สร้างประกาศหาสินค้า
   */
  const createBuyerRequest = async (requestData) => {
    if (!currentUser) throw new Error('กรุณาเข้าสู่ระบบ');
    
    setLoading(true);
    try {
      // ตรวจสอบเหรียญ
      const feeCheck = checkCoinRequirement(coins, 'CREATE_BUYER_REQUEST');
      if (!feeCheck.hasEnough) {
        throw new Error(feeCheck.message);
      }

      // หักเหรียญ
      await deductCoins(feeCheck.required);

      // สร้างประกาศ
      const requestModel = createBuyerRequestModel({
        ...requestData,
        buyerId: currentUser.uid,
        buyerName: currentUser.displayName || currentUser.email,
        feePaid: true
      });

      const requestRef = await addDoc(collection(db, 'buyerRequests'), requestModel);
      return requestRef.id;
    } catch (error) {
      console.error('Error creating buyer request:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    // State
    cart,
    loading,
    
    // Store
    openStore,
    updateStore,
    listProduct,
    
    // Package Subscription
    getStoreSubscription,
    subscribeToPackage,
    cancelSubscription,
    
    // Video Call Add-on
    subscribeToVideoCallAddon,
    cancelVideoCallAddon,
    
    // Cart
    addToCart,
    removeFromCart,
    updateCartQuantity,
    clearCart,
    getCartTotal,
    
    // Order
    createOrder,
    updateOrderStatus,
    
    // Auction
    createAuction,
    placeBid,
    endAuction,
    confirmWinner,
    createOrderFromAuction,
    cancelAuction,
    
    // Buyer Request
    createBuyerRequest
  };

  return (
    <MarketplaceContext.Provider value={value}>
      {children}
    </MarketplaceContext.Provider>
  );
};

export const useMarketplace = () => {
  const context = useContext(MarketplaceContext);
  if (!context) {
    throw new Error('useMarketplace must be used within MarketplaceProvider');
  }
  return context;
};


